import { AiUsageCurrent } from '@dadohr/shared/domainTypes/aiUsage';
import { Doc } from '@dadohr/shared/domainTypes/document';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField
} from '@material-ui/core';
import { truncate } from 'lodash';
import { useMemo, useState } from 'react';
import {
  ItemSorters,
  ROW_HEIGHTS,
  RowsRenderer,
  useSortQueryParam
} from '../../../../../components/GroupableList';
import { Loader } from '../../../../../components/Loader';
import {
  TableSearch,
  TableToolbarSection
} from '../../../../../components/Table';
import { IColumn } from '../../../../../components/Table/VirtualizedSortable';
import { useErrorLogger } from '../../../../../hooks/useErrorLogger';
import { CanvasBar } from '../../../../../layout/Canvas';
import { FlexContainer } from '../../../../../layout/Flex';
import { Section } from '../../../../../layout/Section';
import {
  updateAiUsageLimits,
  useAllAiUsageCurrent
} from '../../../../../services/aiUsage';
import { formatDate, toMoment } from '../../../../../services/time';
import { toSearchTermRegexp } from '../../../../../services/user';
import { useAllOrgs } from '../../../../services/orgs';

type OrgWithUsage = {
  org: Doc<IOrganization>;
  usage: Doc<AiUsageCurrent> | null;
};

const formatNumber = (n: number) => n.toLocaleString();

const formatTokens = (tokens: number) => {
  if (tokens >= 1000000) {
    return `${(tokens / 1000000).toFixed(2)}M`;
  }
  if (tokens >= 1000) {
    return `${(tokens / 1000).toFixed(1)}K`;
  }
  return tokens.toString();
};

const EditLimitsDialog = ({
  open,
  onClose,
  orgId,
  currentLimits
}: {
  open: boolean;
  onClose: () => void;
  orgId: string;
  currentLimits: { tokenLimit?: number; requestLimit?: number };
}) => {
  const [tokenLimit, setTokenLimit] = useState<string>(
    currentLimits.tokenLimit?.toString() || ''
  );
  const [requestLimit, setRequestLimit] = useState<string>(
    currentLimits.requestLimit?.toString() || ''
  );
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateAiUsageLimits(orgId, {
        tokenLimit: tokenLimit ? parseInt(tokenLimit, 10) : null,
        requestLimit: requestLimit ? parseInt(requestLimit, 10) : null
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit AI Usage Limits</DialogTitle>
      <DialogContent>
        <TextField
          label="Token Limit (leave empty for unlimited)"
          fullWidth
          type="number"
          value={tokenLimit}
          onChange={(e) => setTokenLimit(e.target.value)}
          variant="outlined"
          margin="normal"
        />
        <TextField
          label="Request Limit (leave empty for unlimited)"
          fullWidth
          type="number"
          value={requestLimit}
          onChange={(e) => setRequestLimit(e.target.value)}
          variant="outlined"
          margin="normal"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

type ColumnName =
  | 'orgId'
  | 'orgName'
  | 'totalTokens'
  | 'requestCount'
  | 'tokenLimit'
  | 'periodStart';

type D = OrgWithUsage;
type Column = IColumn<D, ColumnName>;

const COLUMNS: Column[] = [
  {
    key: 'orgId',
    head: () => 'Org ID',
    cell: (d) => <div>{truncate(d.org.id, { length: 12 })}</div>,
    align: 'left',
    sortable: true,
    defaultDirection: 'asc',
    width: 120,
    flexGrow: 0
  },
  {
    key: 'orgName',
    head: () => 'Organization',
    cell: (d) => d.org.data.name,
    align: 'left',
    sortable: true,
    defaultDirection: 'asc',
    width: 200,
    flexGrow: 1
  },
  {
    key: 'totalTokens',
    head: () => 'Total Tokens',
    cell: (d) =>
      d.usage ? formatTokens(d.usage.data.totalTokens) : '-',
    align: 'right',
    sortable: true,
    defaultDirection: 'desc',
    width: 120,
    flexGrow: 0
  },
  {
    key: 'requestCount',
    head: () => 'Requests',
    cell: (d) =>
      d.usage ? formatNumber(d.usage.data.requestCount) : '-',
    align: 'right',
    sortable: true,
    defaultDirection: 'desc',
    width: 100,
    flexGrow: 0
  },
  {
    key: 'tokenLimit',
    head: () => 'Token Limit',
    cell: (d) =>
      d.usage?.data.tokenLimit
        ? formatTokens(d.usage.data.tokenLimit)
        : 'Unlimited',
    align: 'right',
    sortable: true,
    defaultDirection: 'desc',
    width: 120,
    flexGrow: 0
  },
  {
    key: 'periodStart',
    head: () => 'Period Start',
    cell: (d) =>
      d.usage?.data.periodStart
        ? formatDate(toMoment(d.usage.data.periodStart), 'YYYY/MM/DD')
        : '-',
    align: 'right',
    sortable: true,
    defaultDirection: 'desc',
    width: 110,
    flexGrow: 0
  }
];

const SORTERS: ItemSorters<D> = {
  orgId: {
    key: 'orgId',
    items: { sort: (d) => d.org.id, dir: 'asc' }
  },
  orgName: {
    key: 'orgName',
    items: { sort: (d) => d.org.data.name, dir: 'asc' }
  },
  totalTokens: {
    key: 'totalTokens',
    items: { sort: (d) => d.usage?.data.totalTokens || 0, dir: 'desc' }
  },
  requestCount: {
    key: 'requestCount',
    items: { sort: (d) => d.usage?.data.requestCount || 0, dir: 'desc' }
  },
  tokenLimit: {
    key: 'tokenLimit',
    items: { sort: (d) => d.usage?.data.tokenLimit || Infinity, dir: 'desc' }
  },
  periodStart: {
    key: 'periodStart',
    items: {
      sort: (d) => d.usage?.data.periodStart?.toMillis() || 0,
      dir: 'desc'
    }
  }
};

const DEFAULT_SORTER = SORTERS.totalTokens;

const ROW_TO_KEY = (d: D) => d.org.id;

export const PageAiUsageOverview = () => {
  const [q, setQ] = useState('');
  const [orgs, orgsLoading, orgsError] = useAllOrgs();
  const [allUsage, usageLoading, usageError] = useAllAiUsageCurrent();
  useErrorLogger('allOrgs', orgsError);
  useErrorLogger('allAiUsage', usageError);
  const [[sorter, dir], setSort] = useSortQueryParam('sort', SORTERS);
  const [editingOrg, setEditingOrg] = useState<{
    id: string;
    limits: { tokenLimit?: number; requestLimit?: number };
  } | null>(null);

  const loading = orgsLoading || usageLoading;

  const usageByOrgId = useMemo(() => {
    if (!allUsage) return {};
    const map: Record<string, Doc<AiUsageCurrent>> = {};
    allUsage.forEach((usage) => {
      map[usage.id] = usage;
    });
    return map;
  }, [allUsage]);

  const orgsWithUsage = useMemo(() => {
    if (!orgs) return null;
    return orgs.map((org) => ({
      org,
      usage: usageByOrgId[org.id] || null
    }));
  }, [orgs, usageByOrgId]);

  const filteredData = useMemo(() => {
    if (!orgsWithUsage) return null;
    if (!q) return orgsWithUsage;
    const regexp = toSearchTermRegexp(q);
    return orgsWithUsage.filter(
      (d) => d.org.id.match(regexp) || d.org.data.name.match(regexp)
    );
  }, [orgsWithUsage, q]);

  return (
    <Section>
      <CanvasBar>
        <FlexContainer>
          <TableSearch
            term={q}
            onChange={setQ}
            placeholder="Search by org ID or name"
          />
        </FlexContainer>
        <TableToolbarSection />
      </CanvasBar>
      {loading && <Loader height={500} />}
      {filteredData && (
        <RowsRenderer
          variant="contained"
          columns={COLUMNS}
          rows={filteredData}
          rowToKey={ROW_TO_KEY}
          rowToHref={(d) => `/ai-usage/${d.org.id}`}
          sorter={sorter || DEFAULT_SORTER}
          sortDirection={dir}
          renderHead={true}
          headProps={{ sticky: true }}
          onHeadClick={(c, d) => setSort([SORTERS[c.key] || DEFAULT_SORTER, d])}
          chunkSize={30}
          rootMargin="300px"
          rowHeight={ROW_HEIGHTS.dense}
          otherProps={undefined}
        />
      )}
      {editingOrg && (
        <EditLimitsDialog
          open={true}
          onClose={() => setEditingOrg(null)}
          orgId={editingOrg.id}
          currentLimits={editingOrg.limits}
        />
      )}
    </Section>
  );
};
